import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCacheVersions } from '../lib/api';
import {
  CACHE_VERSION_FIELDS,
  clearSmartCacheEntry,
  loadWithSmartCache,
  readSmartCache,
} from '../lib/smartCache';

export default function useRemoteData(fetcher, fallback = [], options = {}) {
  const {
    cacheKey,
    cacheStrategy = cacheKey ? 'versioned' : 'none',
    versionField = cacheKey ? CACHE_VERSION_FIELDS[cacheKey] : null,
    shortCacheMs = 30000,
    preserveOnEmpty = false,
    shape = 'array',
  } = options;

  const fallbackRef = useRef(fallback);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const normalizeResult = useCallback(
    (result) => {
      if (shape === 'object') {
        return result && typeof result === 'object' ? result : fallbackRef.current;
      }

      if (Array.isArray(result)) {
        if (preserveOnEmpty && result.length === 0) {
          return fallbackRef.current;
        }
        return result;
      }

      return fallbackRef.current;
    },
    [preserveOnEmpty, shape]
  );

  const [data, setData] = useState(() => {
    const cached = cacheKey ? readSmartCache(cacheKey) : null;
    if (cached !== null && cached !== undefined) {
      return normalizeResult(cached);
    }
    return fallbackRef.current;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    if (cacheKey) {
      clearSmartCacheEntry(cacheKey);
    }
    setReloadKey((current) => current + 1);
  }, [cacheKey]);

  useEffect(() => {
    let ignore = false;

    async function load(forceRefresh = false) {
      try {
        if (cacheStrategy === 'none' || !cacheKey) {
          setLoading(true);
          const result = await fetcherRef.current();
          if (!ignore) {
            setData(normalizeResult(result));
            setFromCache(false);
            setError(null);
          }
          return;
        }

        const cachedData = readSmartCache(cacheKey);
        if (!cachedData && !forceRefresh) {
          setLoading(true);
        }

        const { data: nextData, fromCache: servedFromCache } = await loadWithSmartCache({
          cacheKey,
          versionField,
          strategy: cacheStrategy,
          shortCacheMs,
          fetchData: () => fetcherRef.current(),
          fetchVersions: fetchCacheVersions,
          forceRefresh,
        });

        if (!ignore) {
          setData(normalizeResult(nextData));
          setFromCache(servedFromCache);
          setError(null);
        }
      } catch (err) {
        if (!ignore) {
          setError(err);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    load(reloadKey > 0);

    return () => {
      ignore = true;
    };
  }, [cacheKey, cacheStrategy, versionField, shortCacheMs, normalizeResult, reloadKey]);

  return { data, loading, error, fromCache, reload };
}
